import React from 'react';
import {
	useDispatch,
	useSelector,
} from 'react-redux';

import BillingDetailsForm from '~/components/atoms/forms/forms/BillingDetailsForm';
import FormsList from '~/components/atoms/lists/FormsList';
import InvoiceDetailsForm from '~/components/atoms/forms/forms/InvoiceDetailsForm';
import PaymentMethodForm from '~/components/atoms/forms/forms/PaymentMethodForm';

import useAccountId from '~/hooks/useAccountId';
import useAccountIsDeliveryAddressEnabled from '~/hooks/useAccountIsDeliveryAddressEnabled';
import useAccountIsExtraInvoiceDetailsEnabled from '~/hooks/useAccountIsExtraInvoiceDetailsEnabled';
import useAllowedPaymentMethods from '~/hooks/useAllowedPaymentMethods';
import useLegacyBillingDetails from '~/hooks/useLegacyBillingDetails';

import {
	updateBillingDetails,
} from '~/actions/teams';

import {
	METHOD_CARD,
	METHOD_INVOICE,
	METHOD_PAYPAL,
} from '~/model/payments';

import {
	teamSelector,
} from '~/state/ui/selectors';



const BillingDetailsGroup: React.FC = () => {
	const accountId = useAccountId();

	const accountAllowedPaymentMethods = useAllowedPaymentMethods(accountId);
	const accountIsDeliveryAddressEnabled = useAccountIsDeliveryAddressEnabled(accountId);
	const accountIsExtraInvoiceDetailsEnabled = useAccountIsExtraInvoiceDetailsEnabled(accountId);
	const billingDetails = useLegacyBillingDetails(accountId);
	const dispatch = useDispatch();
	const team = useSelector(teamSelector);

	const handleFormSubmitWithCreditCard = React.useCallback(
		(data) => {
			return dispatch(
				updateBillingDetails(
					accountId,
					{
						payment_method: {
							type: METHOD_CARD,
							token: data.values.recurlyToken,
							three_d_secure_result_token: data.values.threeDSecureToken || null,
						},
					},
				),
			);
		},
		[
			accountId,
			dispatch,
		],
	);

	const handleFormSubmitWithInvoice = React.useCallback(
		() => {
			return dispatch(
				updateBillingDetails(
					accountId,
					{
						payment_method: {
							type: METHOD_INVOICE,
						},
					},
				),
			);
		},
		[
			accountId,
			dispatch,
		],
	);

	const handleFormSubmitWithPaypal = React.useCallback(
		(data) => {
			return dispatch(
				updateBillingDetails(
					accountId,
					{
						payment_method: {
							type: METHOD_PAYPAL,
							token: data.values.recurlyToken,
						},
					},
				),
			);
		},
		[
			accountId,
			dispatch,
		],
	);

	const handleFormSubmitWithoutPaymentMethod = React.useCallback(
		(values) => {
			return dispatch(
				updateBillingDetails(
					accountId,
					{
						address: values.model.address,
						city: values.model.city,
						company_name: values.model.name,
						country: values.model.country,
						email: values.model.email,
						zip_code: values.model.zip_code,
						state: values.model.state || null,
						vat_number: values.model.vatNumber || null,
					},
				),
			);
		},
		[
			accountId,
			dispatch,
		],
	);

	const handleFormSubmitInvoiceDetails = React.useCallback(
		(values) => {
			const deliveryAddressFields = [
				'address',
				'city',
				'company',
				'country',
				'first_name',
				'last_name',
				'state',
				'zip_code',
			];

			deliveryAddressFields.forEach((field) => {
				if (values[field] !== undefined) {
					if (values.delivery_address === undefined) {
						values.delivery_address = {};
					}

					values.delivery_address[field] = values[field];

					delete values[field];
				}
			});

			return dispatch(
				updateBillingDetails(
					accountId,
					values,
				),
			);
		},
		[
			accountId,
			dispatch,
		],
	);

	if (accountAllowedPaymentMethods === null || !billingDetails) {
		return null;
	}

	return (
		<FormsList>
			<BillingDetailsForm
				billingDetails={billingDetails.get('details')}
				submitCallback={handleFormSubmitWithoutPaymentMethod}
				team={team}
			/>

			<PaymentMethodForm
				accountId={accountId}
				allowedPaymentMethods={accountAllowedPaymentMethods}
				billingDetails={billingDetails.get('details')}
				submitWithCreditCardCallback={handleFormSubmitWithCreditCard}
				submitWithInvoiceCallback={handleFormSubmitWithInvoice}
				submitWithPaypalCallback={handleFormSubmitWithPaypal}
			/>

			{(accountIsDeliveryAddressEnabled || accountIsExtraInvoiceDetailsEnabled) && (
				<InvoiceDetailsForm
					billingDetails={billingDetails.get('details')}
					submitCallback={handleFormSubmitInvoiceDetails}
					team={team}
				/>
			)}
		</FormsList>
	);
};



export default BillingDetailsGroup;
